import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, easeOut } from "./Reveal";
import { MENU, IMAGES } from "@/data/menu";

const CAT_IMAGE = {
  rice: IMAGES.idli,
  dose: IMAGES.dose,
  specials: IMAGES.specials,
  chinese: IMAGES.chinese,
  beverages: IMAGES.coffee,
};

export function MenuSection() {
  const [active, setActive] = useState(MENU[0].id);
  const cat = MENU.find((c) => c.id === active);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 md:py-36 bg-surface border-y border-line">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="font-body text-xs uppercase tracking-[0.35em] text-terracotta">
              The Menu
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight mt-4">
              Freshly made, <span className="italic text-olive">all day.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-sm text-ink/55 max-w-xs">
              Kindly allow about 20 minutes — everything is cooked to order.
            </p>
          </Reveal>
        </div>

        {/* Category tabs */}
        <Reveal>
          <div
            data-testid="menu-tabs"
            className="flex flex-wrap gap-3 mb-12"
          >
            {MENU.map((c) => (
              <button
                key={c.id}
                data-testid={`menu-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-5 py-2.5 font-body text-sm tracking-wide transition-colors duration-300 border ${
                  active === c.id
                    ? "bg-olive text-cream border-olive"
                    : "bg-transparent text-ink/70 border-line hover:border-olive hover:text-olive"
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Item list */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: easeOut }}
              >
                <div className="flex items-baseline gap-4 mb-8">
                  <h3 className="font-display text-3xl md:text-4xl text-ink">{cat.title}</h3>
                  <span className="font-kannada text-olive text-lg">{cat.titleKannada}</span>
                </div>
                {cat.note && (
                  <p className="font-body text-sm italic text-terracotta mb-8">{cat.note}</p>
                )}

                <ul data-testid="menu-item-list" className="divide-y divide-line/70">
                  {cat.items.map((item, i) => (
                    <motion.li
                      key={item.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.03, ease: easeOut }}
                      className="py-4 group"
                    >
                      <p className="font-body font-semibold text-ink text-lg group-hover:text-olive transition-colors">
                        {item.name}
                      </p>
                      {item.desc && (
                        <p className="font-body text-sm text-ink/50 mt-0.5">{item.desc}</p>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Spotlight image */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: easeOut }}
                  className="overflow-hidden rounded-2xl border border-line shadow-[0_30px_60px_rgba(78,98,44,0.10)]"
                >
                  <img
                    src={CAT_IMAGE[active]}
                    alt={cat.title}
                    className="w-full h-[380px] lg:h-[520px] object-cover"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
